import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Available themes.
 */
export enum Theme {
  LIGHT = "LIGHT",
  DARK = "DARK",
}

/**
 * User's theme preference - can be explicit (LIGHT/DARK), follow system settings, or no preference.
 * NONE means no user preference is stored - the app/route decides (this clears localStorage).
 */
export enum ThemePreference {
  NONE = "NONE", // No preference - let app/route decide (clears localStorage)
  LIGHT = "LIGHT",
  DARK = "DARK",
  SYSTEM = "SYSTEM",
}

const THEME_KEY = "theme"; // key for local storage of current theme
const USER_PREFERENCE_KEY = "userThemePreference"; // key for user's manual preference
const DEFAULT_THEME = Theme.LIGHT; // default theme if no preference has been selected
const DEFAULT_PREFERENCE = ThemePreference.NONE; // default preference - no stored preference

/**
 * A list of available themes and their associated metadata; includes the theme enum value and a
 * user-facing label.
 */
export const THEMES = [
  {
    value: Theme.LIGHT,
    label: "Light Theme",
  },
  {
    value: Theme.DARK,
    label: "Dark Theme",
  },
];

/**
 * App-level theme context, intended to abstract the internals of switching themes across various
 * design systems (ie. tailwind)
 */
export interface ThemeContext {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  userPreference: ThemePreference;
  setUserPreference: (preference: ThemePreference) => void;
}

const themeContext = React.createContext<ThemeContext>({
  theme: Theme.LIGHT,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setTheme: () => {},
  userPreference: ThemePreference.NONE,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setUserPreference: () => {},
});

/**
 * Combined provider for theme context; should wrap the app's top-most UI component.
 */
export default function ThemeProvider(props: React.PropsWithChildren) {
  const [theme, setTheme] = useState<Theme | null>(null);
  const [userPreference, setUserPreferenceState] = useState<ThemePreference | null>(null);

  useEffect(() => {
    // Load theme only once window is loaded, otherwise localStorage is undefined
    setTheme(loadSavedTheme());
    setUserPreferenceState(loadUserPreference());
  }, [setTheme]);

  const setThemeHelper = useCallback((newTheme: Theme) => {
    updateSavedTheme(newTheme);
    updateDOMMetadata(newTheme);
    setTheme(newTheme);
  }, []);

  const setUserPreferenceHelper = useCallback((preference: ThemePreference) => {
    updateUserPreference(preference);
    setUserPreferenceState(preference);
    // Also apply the theme immediately when user manually changes preference
    const resolvedTheme = resolvePreferenceToTheme(preference);
    updateSavedTheme(resolvedTheme);
    updateDOMMetadata(resolvedTheme);
    setTheme(resolvedTheme);
  }, []);

  // Listen for system theme changes when preference is SYSTEM
  useEffect(() => {
    if (userPreference !== ThemePreference.SYSTEM) {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      const newTheme = getSystemTheme();
      setThemeHelper(newTheme);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [userPreference, setThemeHelper]);

  // Initialize theme/apply DOM metadata
  useEffect(() => {
    // Set theme once it's loaded
    if (theme != null) {
      updateDOMMetadata(theme);
    }
  }, [theme]);

  const contextValue = useMemo(
    () => ({
      theme: theme!,
      setTheme: setThemeHelper,
      userPreference: userPreference ?? DEFAULT_PREFERENCE,
      setUserPreference: setUserPreferenceHelper,
    }),
    [theme, setThemeHelper, userPreference, setUserPreferenceHelper]
  );

  return theme != null ? (
    <themeContext.Provider value={contextValue}>{props.children}</themeContext.Provider>
  ) : null;
}

/**
 * Retrieve the theme context.
 */
export function useTheme(): ThemeContext {
  return useContext(themeContext);
}

/**
 * Get the sider theme to use based on the viewer's selected theme.
 */
export function useSiderTheme(): "light" | "dark" {
  const { theme } = useTheme();
  return theme === Theme.DARK ? "dark" : "light";
}

/**
 * Load the last used theme from local storage, falling back to the default theme as needed.
 */
function loadSavedTheme(): Theme {
  const savedTheme = localStorage.getItem(THEME_KEY) ?? "";
  if (savedTheme in Theme) {
    return savedTheme as Theme;
  } else {
    updateSavedTheme(DEFAULT_THEME);
    return DEFAULT_THEME;
  }
}

/**
 * Update the theme saved in local storage.
 */
function updateSavedTheme(theme: Theme) {
  localStorage.setItem(THEME_KEY, theme);
}

/**
 * Load the user's manual theme preference from local storage.
 * If no preference is stored, returns NONE (no preference set).
 */
function loadUserPreference(): ThemePreference {
  const savedPreference = localStorage.getItem(USER_PREFERENCE_KEY);
  // No stored preference = NONE (let app/route decide)
  if (savedPreference == null) {
    return ThemePreference.NONE;
  }
  // Check if it's a valid preference (excluding NONE which shouldn't be stored)
  if (savedPreference in ThemePreference && savedPreference !== ThemePreference.NONE) {
    return savedPreference as ThemePreference;
  }
  // Invalid value stored - clear it and return NONE
  clearUserPreference();
  return ThemePreference.NONE;
}

/**
 * Update the user's manual theme preference in local storage.
 * NONE preference clears the localStorage key entirely.
 */
function updateUserPreference(preference: ThemePreference) {
  if (preference === ThemePreference.NONE) {
    clearUserPreference();
  } else {
    localStorage.setItem(USER_PREFERENCE_KEY, preference);
  }
}

/**
 * Clear the user's theme preference from local storage.
 */
function clearUserPreference() {
  localStorage.removeItem(USER_PREFERENCE_KEY);
}

/**
 * Get the system's preferred color scheme.
 */
function getSystemTheme(): Theme {
  if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return Theme.DARK;
  }
  return Theme.LIGHT;
}

/**
 * Resolve a ThemePreference to an actual Theme value.
 * NONE defaults to LIGHT (app default), SYSTEM uses OS preference.
 */
export function resolvePreferenceToTheme(preference: ThemePreference): Theme {
  switch (preference) {
    case ThemePreference.NONE:
      return DEFAULT_THEME; // No preference = use app default (light)
    case ThemePreference.LIGHT:
      return Theme.LIGHT;
    case ThemePreference.DARK:
      return Theme.DARK;
    case ThemePreference.SYSTEM:
      return getSystemTheme();
  }
}

/**
 * Updates DOM metadata to ensure the appropriate themes are provided.
 */
function updateDOMMetadata(theme: Theme) {
  document.documentElement.setAttribute("data-theme", theme.toLowerCase());
}
